import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/design-system/layout/AppLayout";
import {
  Plus,
  ZoomIn,
  ZoomOut,
  Maximize,
  Eye,
  Save,
  ChevronDown,
  Settings,
  Trash2,
  Copy,
  Sparkles,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";

/* ──────────── Types ──────────────────── */
type Section = "basic" | "venue" | "registration" | "divisions" | "addons" | "coupons" | "forms" | "sponsors" | "waiver" | "communications" | "schedule" | "settings";

interface FlowNode {
  id: string;
  type: "start" | "form" | "branch" | "payment" | "addon" | "waiver" | "confirmation";
  title: string;
  description: string;
  x: number;
  y: number;
  inputs: number;
  outputs: number;
  config?: Record<string, any>;
}

interface FlowConnection {
  from: string;
  to: string;
  label?: string;
}

const DEFAULT_FLOW: { nodes: FlowNode[]; connections: FlowConnection[] } = {
  nodes: [
    { id: "start", type: "start", title: "Start", description: "Registration begins", x: 50, y: 150, inputs: 0, outputs: 1 },
    { id: "team-info", type: "form", title: "Team Info", description: "Collects: name, email, division", x: 250, y: 100, inputs: 1, outputs: 1 },
    { id: "division-select", type: "form", title: "Division Select", description: "Choose age group", x: 450, y: 100, inputs: 1, outputs: 1 },
    { id: "roster-size", type: "form", title: "Roster Size", description: "How many players?", x: 650, y: 100, inputs: 1, outputs: 1 },
    { id: "addons", type: "addon", title: "Add-ons", description: "Optional extras", x: 850, y: 100, inputs: 1, outputs: 1 },
    { id: "waiver", type: "waiver", title: "Waiver", description: "Sign waiver", x: 1050, y: 100, inputs: 1, outputs: 1 },
    { id: "payment", type: "payment", title: "Payment", description: "Process payment", x: 1250, y: 100, inputs: 1, outputs: 1 },
    { id: "confirmation", type: "confirmation", title: "Confirmation", description: "Registration complete", x: 1450, y: 150, inputs: 1, outputs: 0 },
  ],
  connections: [
    { from: "start", to: "team-info" },
    { from: "team-info", to: "division-select" },
    { from: "division-select", to: "roster-size" },
    { from: "roster-size", to: "addons" },
    { from: "addons", to: "waiver" },
    { from: "waiver", to: "payment" },
    { from: "payment", to: "confirmation" },
  ],
};

const NODE_TYPES: Record<string, { icon: string; color: string; label: string }> = {
  start: { icon: "▶️", color: "bg-muted", label: "Start" },
  form: { icon: "📋", color: "bg-white border-primary border-2", label: "Form" },
  branch: { icon: "◆", color: "bg-secondary", label: "Branch" },
  payment: { icon: "💳", color: "bg-info-light", label: "Payment" },
  addon: { icon: "🎁", color: "bg-white border-border", label: "Add-on" },
  waiver: { icon: "📄", color: "bg-white border-border", label: "Waiver" },
  confirmation: { icon: "✓", color: "bg-success-light", label: "Confirmation" },
};

const SECTIONS: Array<{ id: Section; label: string; icon: string }> = [
  { id: "basic", label: "Basic Info", icon: "📋" },
  { id: "venue", label: "Venue", icon: "📍" },
  { id: "registration", label: "Registration", icon: "✓" },
  { id: "divisions", label: "Divisions & Age Groups", icon: "👥" },
  { id: "addons", label: "Add-ons & Extras", icon: "🎁" },
  { id: "coupons", label: "Coupons", icon: "🎟️" },
  { id: "forms", label: "Forms", icon: "📝" },
  { id: "sponsors", label: "Sponsors", icon: "🤝" },
  { id: "waiver", label: "Waiver", icon: "📄" },
  { id: "communications", label: "Communications", icon: "📧" },
  { id: "schedule", label: "Schedule Requests", icon: "📅" },
  { id: "settings", label: "Settings", icon: "⚙️" },
];

/* ──────────── Configuration Sections ──────────────────── */
function BasicInfoSection() {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Event Type</label>
        <select className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20">
          <option>Tournament</option>
          <option>Showcase</option>
          <option>Camp</option>
          <option>Clinic</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Sport</label>
        <input
          type="text"
          placeholder="e.g., Basketball, Volleyball, Soccer"
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Start Date</label>
          <input type="date" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">End Date</label>
          <input type="date" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Description</label>
        <textarea
          placeholder="Tell us about this event..."
          rows={4}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
        />
      </div>
    </div>
  );
}

function VenueSection() {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Venue Name</label>
        <input type="text" placeholder="Select or create a venue" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">City</label>
          <input type="text" placeholder="Kansas City" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">State</label>
          <input type="text" placeholder="MO" maxLength={2} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
        </div>
      </div>
      <button className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium">
        <Plus className="w-4 h-4" />
        Add New Venue
      </button>
    </div>
  );
}

function RegistrationSection() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Registration Opens</label>
          <input type="date" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Registration Closes</label>
          <input type="date" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Entry Fee</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
          <input type="number" placeholder="695.00" className="w-full rounded-lg border border-border bg-background pl-7 pr-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Registration Type</label>
        <div className="space-y-2">
          {["Team-based", "Individual", "Hybrid"].map((type) => (
            <label key={type} className="flex items-center gap-2">
              <input type="radio" name="regType" className="rounded" />
              <span className="text-sm text-foreground">{type}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function DivisionsSection() {
  return (
    <div className="space-y-4">
      <div className="space-y-3 mb-4">
        {["14U", "15U", "16U", "17U"].map((div) => (
          <div key={div} className="flex items-center justify-between p-3 border border-border rounded-lg">
            <span className="text-sm font-medium text-foreground">{div}</span>
            <button className="text-muted-foreground hover:text-foreground transition-colors">×</button>
          </div>
        ))}
      </div>
      <button className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium">
        <Plus className="w-4 h-4" />
        Add Division
      </button>
    </div>
  );
}

function CollapsibleSection({
  section,
  isOpen,
  onToggle,
}: {
  section: { id: Section; label: string; icon: string };
  isOpen: boolean;
  onToggle: () => void;
}) {
  const renderContent = () => {
    switch (section.id) {
      case "basic":
        return <BasicInfoSection />;
      case "venue":
        return <VenueSection />;
      case "registration":
        return <RegistrationSection />;
      case "divisions":
        return <DivisionsSection />;
      default:
        return <div className="p-4 bg-secondary rounded-lg text-sm text-muted-foreground">{section.label} section coming soon...</div>;
    }
  };

  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-4 hover:bg-secondary transition-colors">
        <div className="flex items-center gap-3">
          <span className="text-lg">{section.icon}</span>
          <h3 className="font-semibold text-foreground text-left">{section.label}</h3>
        </div>
        <ChevronDown className={cn("w-5 h-5 text-muted-foreground transition-transform", { "rotate-180": isOpen })} />
      </button>
      {isOpen && <div className="border-t border-border p-4 bg-card">{renderContent()}</div>}
    </div>
  );
}

/* ──────────── Flow Node Card Component ──────────────────── */
function FlowNodeCard({
  node,
  isSelected,
  onSelect,
}: {
  node: FlowNode;
  isSelected: boolean;
  onSelect: (id: string) => void;
}) {
  const nodeType = NODE_TYPES[node.type];
  const isPinned = node.type === "start" || node.type === "confirmation";

  return (
    <div
      onClick={() => onSelect(node.id)}
      className={cn(
        "absolute rounded-xl shadow-sm transition-all duration-150 cursor-pointer group",
        node.type === "start" || node.type === "confirmation" ? "w-32 h-20 rounded-full" : "w-40 h-24",
        nodeType.color,
        isSelected ? "ring-2 ring-primary shadow-md" : "hover:shadow-md"
      )}
      style={{ left: `${node.x}px`, top: `${node.y}px` }}
    >
      <div className="flex flex-col items-center justify-center h-full p-3 text-center">
        <span className="text-2xl mb-1">{nodeType.icon}</span>
        <h4 className="text-xs font-bold text-foreground leading-tight">{node.title}</h4>
        {(node.type === "form" || node.type === "addon" || node.type === "waiver") && (
          <p className="text-xs text-muted-foreground mt-1 leading-tight">{node.description}</p>
        )}
      </div>

      {isSelected && !isPinned && (
        <div className="absolute -top-8 left-0 right-0 flex justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button className="p-1 rounded bg-white border border-border hover:bg-secondary text-foreground">
            <Copy size={12} />
          </button>
          <button className="p-1 rounded bg-white border border-border hover:bg-secondary text-destructive">
            <Trash2 size={12} />
          </button>
        </div>
      )}

      {node.inputs > 0 && <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-border border-2 border-white" />}
      {node.outputs > 0 && <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-border border-2 border-white" />}
    </div>
  );
}

/* ──────────── Connection Line SVG ──────────────────── */
function ConnectionLines({ nodes, connections }: { nodes: FlowNode[]; connections: FlowConnection[] }) {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible" style={{ zIndex: 0 }}>
      {connections.map((conn, i) => {
        const fromNode = nodeMap.get(conn.from);
        const toNode = nodeMap.get(conn.to);
        if (!fromNode || !toNode) return null;

        const fromX = fromNode.x + (fromNode.type === "start" || fromNode.type === "confirmation" ? 64 : 80);
        const fromY = fromNode.y + (fromNode.type === "start" || fromNode.type === "confirmation" ? 40 : 48);
        const toX = toNode.x;
        const toY = toNode.y + (toNode.type === "start" || toNode.type === "confirmation" ? 40 : 48);
        const midX = (fromX + toX) / 2;

        return (
          <path
            key={i}
            d={`M ${fromX} ${fromY} C ${midX} ${fromY}, ${midX} ${toY}, ${toX} ${toY}`}
            stroke="hsl(var(--border))"
            strokeWidth="2"
            fill="none"
            markerEnd="url(#arrowhead)"
          />
        );
      })}
      <defs>
        <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <polygon points="0 0, 10 3, 0 6" fill="hsl(var(--border))" />
        </marker>
      </defs>
    </svg>
  );
}

/* ──────────── Properties Panel ──────────────────── */
function PropertiesPanel({
  selectedNode,
  nodes,
}: {
  selectedNode: FlowNode | null;
  nodes: FlowNode[];
}) {
  if (!selectedNode) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6 text-center border-l border-border">
        <Settings className="w-12 h-12 text-muted-foreground mb-4" />
        <p className="text-sm text-muted-foreground">Click a node to edit its properties</p>
      </div>
    );
  }

  const isPinned = selectedNode.type === "start" || selectedNode.type === "confirmation";

  return (
    <div className="h-full flex flex-col border-l border-border p-6 overflow-y-auto">
      <h3 className="font-bold text-foreground mb-4">
        {selectedNode.title} {isPinned && "📌"}
      </h3>

      <div className="mb-4 p-3 rounded-lg bg-secondary text-sm text-foreground">
        Type: <span className="font-medium">{selectedNode.type}</span>
      </div>

      {selectedNode.type === "form" && (
        <div className="space-y-4 mb-4">
          <div>
            <label className="text-xs font-medium text-foreground mb-1 block">Fields</label>
            <div className="space-y-2 p-3 rounded-lg bg-secondary">
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" defaultChecked />
                Name (required)
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" defaultChecked />
                Email (required)
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" defaultChecked />
                Phone number
              </label>
            </div>
          </div>
        </div>
      )}

      {selectedNode.type === "branch" && (
        <div className="space-y-4 mb-4">
          <div>
            <label className="text-xs font-medium text-foreground mb-1 block">Condition</label>
            <select className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20">
              <option>Division equals...</option>
              <option>Age group is...</option>
              <option>Team size exceeds...</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-foreground mb-1 block">Then go to</label>
            <select className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20">
              <option>Select step...</option>
              {nodes
                .filter((n) => n.id !== selectedNode.id)
                .map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.title}
                  </option>
                ))}
            </select>
          </div>
        </div>
      )}

      {selectedNode.type === "payment" && (
        <div className="space-y-4 mb-4">
          <div>
            <label className="text-xs font-medium text-foreground mb-1 block">Amount</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <input
                type="number"
                placeholder="695.00"
                defaultValue="695"
                className="w-full rounded-lg border border-border bg-background pl-7 pr-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-foreground mb-1 block">Accepted methods</label>
            <div className="space-y-2 p-3 rounded-lg bg-secondary">
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" defaultChecked />
                Credit Card
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" defaultChecked />
                ACH
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" />
                Check
              </label>
            </div>
          </div>
        </div>
      )}

      {!isPinned && (
        <button className="mt-auto w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-error-light text-error hover:bg-error/20 transition-colors font-medium text-sm">
          <Trash2 size={16} />
          Delete Step
        </button>
      )}
    </div>
  );
}

/* ──────────── Main Flow Builder with Sections ──────────────────── */
export default function FlowBuilder() {
  const navigate = useNavigate();
  const [nodes, setNodes] = useState<FlowNode[]>(DEFAULT_FLOW.nodes);
  const [connections] = useState<FlowConnection[]>(DEFAULT_FLOW.connections);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [eventName, setEventName] = useState("Summer Basketball Classic");
  const [openSections, setOpenSections] = useState<Set<Section>>(new Set(["basic"]));
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(true);

  const selectedNode = nodes.find((n) => n.id === selectedNodeId);

  const handleZoom = (direction: "in" | "out") => {
    setZoom((prev) => (direction === "in" ? Math.min(prev + 0.2, 2) : Math.max(prev - 0.2, 0.5)));
  };

  const handleFitToView = () => {
    setZoom(0.8);
    setPanX(0);
    setPanY(0);
  };

  const addStep = (type: FlowNode["type"]) => {
    const newNode: FlowNode = {
      id: `node-${Date.now()}`,
      type,
      title: NODE_TYPES[type].label,
      description: "New step",
      x: 200 + Math.random() * 100,
      y: 200 + Math.random() * 100,
      inputs: type === "start" ? 0 : 1,
      outputs: type === "confirmation" ? 0 : 1,
    };
    setNodes([...nodes, newNode]);
  };

  const toggleSection = (id: Section) => {
    const newOpen = new Set(openSections);
    if (newOpen.has(id)) {
      newOpen.delete(id);
    } else {
      newOpen.add(id);
    }
    setOpenSections(newOpen);
  };

  return (
    <AppLayout userName="Sarah Johnson" userRole="Tournament Operator">
      <div className="h-screen bg-background flex overflow-hidden">
        {/* Left Sidebar - Event Configuration Sections */}
        <div
          className={cn(
            "bg-card border-r border-border overflow-y-auto transition-all duration-300 flex flex-col",
            leftSidebarOpen ? "w-96" : "w-0"
          )}
        >
          {leftSidebarOpen && (
            <div className="flex-1 min-w-0 p-6 space-y-4">
              <div className="mb-4 sticky top-0 bg-card pb-4">
                <h2 className="text-lg font-bold text-foreground">Event Setup</h2>
                <p className="text-xs text-muted-foreground">Configure your event details</p>
              </div>

              <div className="space-y-3">
                {SECTIONS.map((section) => (
                  <CollapsibleSection
                    key={section.id}
                    section={section}
                    isOpen={openSections.has(section.id)}
                    onToggle={() => toggleSection(section.id)}
                  />
                ))}
              </div>

              {/* Footer Actions */}
              <div className="flex gap-2 mt-8 pt-4 border-t border-border sticky bottom-0 bg-card py-4">
                <button
                  onClick={() => navigate("/events")}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-border text-foreground hover:bg-secondary transition-colors font-medium text-sm"
                >
                  Cancel
                </button>
                <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-white hover:opacity-90 transition-opacity font-medium text-sm">
                  <Save className="w-4 h-4" />
                  Save & Build
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Main Canvas Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Toolbar */}
          <div className="border-b border-border bg-white p-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLeftSidebarOpen(!leftSidebarOpen)}
                className="p-2 rounded-lg hover:bg-secondary transition-colors text-foreground"
                title={leftSidebarOpen ? "Hide event setup" : "Show event setup"}
              >
                <ChevronDown className={cn("w-5 h-5 transition-transform", leftSidebarOpen ? "rotate-90" : "-rotate-90")} />
              </button>

              <div className="relative">
                <button className="px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90 transition-opacity font-medium text-sm flex items-center gap-2">
                  <Plus size={16} />
                  Add Step
                </button>
                <div className="hidden absolute top-full left-0 mt-1 w-48 rounded-lg border border-border bg-card shadow-md py-2 z-50">
                  {Object.entries(NODE_TYPES)
                    .filter(([k]) => !["start", "confirmation"].includes(k))
                    .map(([type, config]) => (
                      <button
                        key={type}
                        onClick={() => addStep(type as FlowNode["type"])}
                        className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-secondary transition-colors flex items-center gap-2"
                      >
                        <span className="text-lg">{config.icon}</span>
                        {config.label}
                      </button>
                    ))}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button onClick={() => handleZoom("in")} className="p-2 rounded-lg hover:bg-secondary transition-colors text-foreground" title="Zoom in">
                <ZoomIn size={18} />
              </button>
              <button onClick={() => handleZoom("out")} className="p-2 rounded-lg hover:bg-secondary transition-colors text-foreground" title="Zoom out">
                <ZoomOut size={18} />
              </button>
              <button onClick={handleFitToView} className="p-2 rounded-lg hover:bg-secondary transition-colors text-foreground" title="Fit to view">
                <Maximize size={18} />
              </button>
              <div className="text-xs text-muted-foreground mx-2">{Math.round(zoom * 100)}%</div>

              <div className="h-6 w-px bg-border mx-2" />

              <button
                onClick={() => setShowPreview(true)}
                className="px-4 py-2 rounded-lg bg-secondary text-foreground hover:bg-muted transition-colors text-sm font-medium flex items-center gap-2"
              >
                <Eye size={16} />
                Preview Flow
              </button>
              <button className="px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90 transition-opacity text-sm font-medium flex items-center gap-2">
                <Save size={16} />
                Publish
              </button>
              <span className="text-xs text-muted-foreground mx-2">Saved 2 min ago</span>
            </div>
          </div>

          {/* Canvas Area */}
          <div className="flex-1 flex overflow-hidden relative bg-white">
            <div
              className="flex-1 relative overflow-auto"
              style={{
                transform: `scale(${zoom}) translate(${panX}px, ${panY}px)`,
                transformOrigin: "0 0",
              }}
            >
              {/* Grid background */}
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px)",
                  backgroundSize: "40px 40px",
                  pointerEvents: "none",
                }}
              />

              {/* Connections */}
              <ConnectionLines nodes={nodes} connections={connections} />

              {/* Nodes */}
              <div className="relative w-full h-full" style={{ minHeight: "800px", minWidth: "1600px" }}>
                {nodes.map((node) => (
                  <FlowNodeCard
                    key={node.id}
                    node={node}
                    isSelected={selectedNodeId === node.id}
                    onSelect={setSelectedNodeId}
                  />
                ))}
              </div>
            </div>

            {/* Minimap */}
            <div className="absolute bottom-4 right-4 w-48 h-32 border border-border rounded-lg bg-secondary p-2 shadow-sm">
              <div className="text-xs text-muted-foreground mb-2 font-medium">Minimap</div>
              <div className="w-full h-full rounded border border-border bg-white relative overflow-hidden flex items-center justify-center">
                <div className="text-xs text-muted-foreground">🗺️</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Sidebar - Properties Panel */}
        <div className="w-80 bg-card border-l border-border overflow-hidden flex flex-col">
          <PropertiesPanel selectedNode={selectedNode || null} nodes={nodes} />
        </div>

        {/* Preview Sheet */}
        {showPreview && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
            <div className="bg-card rounded-2xl w-full max-w-2xl max-h-screen overflow-auto">
              <div className="sticky top-0 border-b border-border bg-card p-6 flex items-center justify-between">
                <h2 className="text-xl font-bold text-foreground">Registration Flow Preview</h2>
                <button onClick={() => setShowPreview(false)} className="text-foreground hover:text-muted-foreground">
                  <X size={20} />
                </button>
              </div>
              <div className="p-6 space-y-6">
                <div className="text-sm text-muted-foreground">
                  <p className="mb-4">
                    <strong>Step 1 of 8:</strong> Team Info
                  </p>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full w-1/8 bg-primary rounded-full" />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Team Name *</label>
                    <input
                      type="text"
                      placeholder="e.g., KC Heat 16U"
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Email *</label>
                    <input
                      type="email"
                      placeholder="coach@example.com"
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6 pt-4 border-t border-border">
                  <button className="px-4 py-2 rounded-lg border border-border text-foreground hover:bg-secondary transition-colors font-medium text-sm">
                    Back
                  </button>
                  <button className="ml-auto px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90 transition-opacity font-medium text-sm">
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
